import React from 'react';
import { Box, BoxProps, SxProps, Theme } from '@mui/material';
import { useInteractionContext } from '@components/ElementAnalytics/InteractionContext';
import Typography from '@components/Atoms/Typography';

export interface SectionWrapperProps extends Omit<BoxProps, 'children'> {
  /** The title of the section */
  title: string;
  /** The description or purpose of the section */
  description?: string;
  /** The content to be wrapped */
  children: React.ReactNode;
  /** Optional control rendered top-right of the header, level with the title */
  action?: React.ReactNode;
  /** Unique test identifier for testing purposes */
  uniqueTestId?: string;
}

/**
 * A wrapper component that provides a consistent structure for sections with title and description.
 * This component helps users understand the purpose and content of each section.
 * 
 * @param title - The main title of the section
 * @param description - Optional description explaining the section's purpose
 * @param children - The content to be wrapped
 * @param action - Optional control rendered top-right of the header
 * @param uniqueTestId - Optional unique identifier for testing
 * @returns A wrapped section with title, description, and content
 */
const SectionWrapper = ({
  title,
  description,
  children,
  action,
  uniqueTestId,
  sx,
  ...rest
}: SectionWrapperProps) => {
  const { getTestId } = useInteractionContext();

  const defaultSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'fit-content',
    minHeight: '37.5rem',
    p: '2rem',
    mb: '2rem',
    backgroundColor: '#ffffff',
    borderRadius: '0.5rem',
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
  };

  const mergedSx: SxProps<Theme> = sx ? { ...defaultSx, ...sx } : defaultSx;

  return (
    <Box 
      data-testid={getTestId(uniqueTestId || 'section-wrapper')} 
      sx={mergedSx}
      {...rest}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1.5rem',
          mb: '2rem',
          textAlign: 'left',
          maxWidth: '100%',
          width: '100%',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h2"
            sx={{ mb: '1rem', fontWeight: 'bold', color: '#282828' }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.9rem' }}
            >
              {description}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flex: 'none' }}>{action}</Box>}
      </Box>
      <Box sx={{ width: '100%' }}>
        {children}
      </Box>
    </Box>
  );
};

export default SectionWrapper;
